import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subject, Subscription } from 'rxjs';

import { platformService } from './platform';
import { httpService } from './http';
import { Configuration, configService } from './localcfg';
import { languageService} from './language';
import { UserMode, stateService } from './state';

/**
 * Represents the different views available in the application.
 * - LOGIN: View login screen
 * - USER: View user screen (select place)
 * - PLACE: View place screen
 * - GUEST: Guest (QR) access
 */
export type ViewMode = 'LOGIN' | 'USER' | 'PLACE' | 'GUEST';

/**
 * Represents the different modes for editing within the application.
 * - VIEW: View place activity
 * - EDIT: Configure place parameters
 * - CART: Creating a new ticket
 */
export type EditMode =  'VIEW' | 'EDIT' | 'CART';

/**
 * Optional per-app defaults for layout mode and zoom.
 * `null` fields keep the historical POS/QRA behaviour (cache / auto).
 */
export type AppProfile = {
    mode: 'mobile' | 'desktop' | null;
    zoom: number | null;
};

/**
 * Defines the structure for managing panels within the application.
 */
export type PlacePanel = {
    main?: string | null,
    scnd?: string | null,
    args?: Record<string, any> | null,
    force?: boolean,
    sync?: ((...args: any[]) => any) | null,
}

export enum UppViewConfiguration {
    /**
     * Represents the key for the device kiosk view mode.
     * This determines whether the kiosk keyboard should be displayed or not
     * - **Default**: `'false'`
     */
    kiosk = 'kiosk',

    /**
     * Represents the key for the device forced view mode (`'mobile'` / `'desktop'` / `'auto'`).
     * Persists the operator layout preference for this device.
     */
    forcedevice = 'forcedevice',

    /**
     * Represents the key for the device theme.
     * Persists the device theme (`'dark'` / `'light'`) across places and reloads.
     */
    theme = 'theme',

    /**
     * Represents the key for the device zoom level.
     * Persists the device zoom level (0.1–1.0) across places and reloads.
     */
    zoom = 'zoom',

    /**
     * Represents the key for the device scrollbar visibility.
     * Persists the device scrollbar preference across places and reloads.
     */
    scrollbar = 'scrollbar'
}

/************************************/
/* VIEW SERVICE                     */
/************************************/

/**
 * @class viewService
 * @description This service is responsible for managing various view-related aspects of the application,
 * including device considerations, access levels, view modes, right panel status, tab status, and service worker updates.
 */
@Injectable({
    providedIn: 'root'
})
export class viewService implements OnDestroy {
    private _subscriptions: Array <Subscription> = [];
    private _langModules = new Set <string> ();

    private get configuration(): Configuration {
        return this.config.getConfiguration('DEVICE_VIEW');
    }

    /******************************/
    /* DEVICE CONSIDERATIONS      */
    /******************************/

    /**
     * @description Determines if zooming is allowed on the device. Zoom is disabled on iOS Safari due to stability issues.
     * @returns {boolean} True if zoom is allowed; otherwise, false.
     */    
    get CanZoom(): boolean{
        // do not apply zoom on ios safari (keeps crashing)  
        return !(this.platform.LegacyMobile) || (this.platform.Browser != 'Safari');
    }

    /**
     * Gets or sets the current theme of the platform.
     * Guest access follows {@link platformService._osTheme}; staff uses a stored preference or the OS theme.
     */
    get Theme(): 'dark' | 'light' | null {
        return this.platform._theme;
    }

    set Theme(value: 'dark' | 'light'){
        if (this.state.Access == 'GUEST'){
            return;
        }

        this.configuration.set(UppViewConfiguration.theme, value);
        this.platform._theme = value;
    }

    /** 
     * @returns {boolean} True if the current theme is light.
     */    
    get LightTheme(): boolean {
        return (this.Theme == 'light');
    }

    /** 
     * @returns {boolean} True if the current theme is dark.
     */    
    get DarkTheme(): boolean{
        return (this.Theme == 'dark');
    }

    /** 
     * @description Observable that emits when the theme changes.
     */    
    get OnTheme(): Observable <null> {
        return this.platform.OnThemeChanged;
    }

    /**
     * @description Gets or sets the scrollbar visibility status on the platform.
     */    
    get Scrollbar(): boolean {
        return this.platform._scrollbar;
    }

    set Scrollbar(value: boolean){
        this.configuration.set(UppViewConfiguration.scrollbar, value);
        setTimeout(() => {
            this.platform._scrollbar = value;
        }, 0);
    }

    /**
     * @description Gets or sets the application zoom level (0.1–1.0). Delegates to platform CSS scaling.
     */
    get ZoomLevel(): number {
        return this.platform._zoomLevel || 1;
    }

    set ZoomLevel(value: number){
        if ((value >= 0.1) && (value <= 1.0)){
            this.configuration.set(UppViewConfiguration.zoom, value);
            setTimeout(() => {
                this.platform._zoomLevel = value;
            }, 0);
        }
    }

    /** 
     * @returns {boolean} True if the device is mobile.
     */    
    get Mobile(): boolean{ 
        return this.platform._isMobile;
    }

    /** 
     * @returns {boolean} True if the device is desktop.
     */    
    get Desktop(): boolean {
        return this.platform._isDesktop;
    }

    /**
     * @returns {boolean} True if the device is classified as legacy.
     */    
    get Legacy(): boolean {
        return (this.platform._isMobile && this.platform.LegacyMobile) || (this.platform._isDesktop && this.platform.LegacyDesktop);
    }

    /**
     * True when the device has no OS on-screen keyboard, so virtual-keyboard
     * (kiosk) mode may be enabled. False on {@link platformService.LegacyMobile}
     * and {@link platformService.LegacyTablet}.
     */
    get CanKiosk(): boolean {
        return !this.platform.LegacyMobile && !this.platform.LegacyTablet;
    }

    /**
     * @description Gets or sets the kiosk mode status of the device.
     * Always false / ignored when {@link CanKiosk} is false (native keyboard).
     */    
    get Kiosk(): boolean {
        if (!this.CanKiosk){
            return false;
        }
        
        return this.configuration.get(UppViewConfiguration.kiosk) as boolean || this.platform._isKiosk;
    }

    set Kiosk(value: boolean){
        if (!this.CanKiosk){
            this.configuration.set(UppViewConfiguration.kiosk, false);
            return;
        }

        this.configuration.set(UppViewConfiguration.kiosk, value);
        setTimeout(() => {
            if (this.platform._isKiosk != value){
                this.platform._isKiosk = value;
            }
        }, 0);
    }

    /**
     * @description Observable that emits when kiosk mode status changes.
     */    
    get OnKiosk(): Observable <null>{
        return this.platform.OnKioskChanged;
    }

    /**
     * @description Gets or sets the operator layout preference for this device
     * (`'mobile'` / `'desktop'` / `'auto'`). Cached in `DEVICE_VIEW`; the setter applies it through
     * `platform` (which navigates with `?mode=` and reloads when the effective mode changes).
     * `'auto'` clears any force and uses hardware detection at session start.
     * On startup, an explicit `?mode=mobile|desktop` in the URL wins over this cache for that load
     * when the cache is not `'auto'`; with `'auto'`, detection (or `?mode=auto`) applies.
     */
    get ForcedMode(): 'mobile' | 'desktop' | 'auto' {
        const _value = this.configuration.get(UppViewConfiguration.forcedevice) as 'mobile' | 'desktop' | 'auto' | null;
        if (_value === 'mobile' || _value === 'desktop' || _value === 'auto'){
            return _value;
        }

        return 'auto';
    }

    set ForcedMode(value: 'mobile' | 'desktop' | 'auto'){
        this.configuration.set(UppViewConfiguration.forcedevice, value);
        this.platform._viewMode = value;
    }

    /******************************/
    /* APPLICATION PROFILE        */
    /******************************/

    private _appProfile: AppProfile = { mode: null, zoom: null };

    /**
     * Per-app defaults for forced layout and first-run zoom.
     * Set by the app shell before or after `CheckDevice`; the setter re-applies
     * so order with `platform.ready()` does not matter.
     */
    get AppProfile(): AppProfile {
        return this._appProfile;
    }

    set AppProfile(value: AppProfile) {
        this._appProfile = {
            mode: value?.mode ?? null,
            zoom: value?.zoom ?? null,
        };
        this._ApplyAppProfile();
    }

    /**
     * Clamps a zoom percentage for UI controls (same rule as place device settings).
     * @param value - Raw percent (e.g. from `ion-range`).
     * @param min - Inclusive floor (default 25).
     * @param max - Inclusive ceiling (default 100).
     */
    NormalizeZoomPercent(value: number, min = 25, max = 100): number {
        const _zoom = Number.isFinite(value) ? value : 100;
        return Math.max(min, Math.min(max, _zoom));
    }

    /******************************/
    /* ACCESS / VIEW              */
    /******************************/

    private _View: ViewMode | null  = null;
    private _onViewChanged = new Subject<void>();

    /** 
     * @description Observable that emits when the view changes.
     */    
    public OnViewChanged = this._onViewChanged.asObservable();
    
    /**
     * @description Forces a refresh by emitting a view change.
     */    
    async Refresh(): Promise <void> {
        this._onViewChanged.next();
    }

    /******************************************/
    /* VIEW:                                  */
    /* Determines the current view screen     */
    /******************************************/

    /** 
     * @description Gets or sets the current view mode.
     * A non-null view is stored immediately but not exposed until
     * {@link platformService.IsReady}, so shells keep the splash until then.
     */    
    get View(): ViewMode | null {
        if (!this.platform.IsReady){
            return null;
        }

        return this._View;
    }

    set View(value: ViewMode | null){
        if (this._View == value){
            return;
        }

        console.info("[VIEW] New view '" + value + "' requested")
        this._View = value;

        if (!this.platform.IsReady){
            return;
        }

        this._onViewChanged.next();
    }

    /******************************************/
    /* ACCESS:                                */
    /* Determines the current user access     */
    /******************************************/
    
    /** 
     * @description Gets or sets the current user access level.
     * Sets the entry {@link View} (`LOGIN` or `GUEST`) once the platform is ready.
     * Session screens (`USER` / `PLACE`) are opened by data/session flow, not here.
     */    
    get Access(): UserMode |null {
        return this.View ? this.state.Access : null;
    }

    set Access(value: UserMode){
        this.state.Access = value;

        if (this._View !== 'USER' && this._View !== 'PLACE'){
            switch(value){
                case 'GUEST':
                    this.View = 'GUEST';
                    break;
                case 'LOGIN':
                    this.View = 'LOGIN';
                    break;
            }
        }

        void this._LoadLanguages();
    }

    /******************************************/
    /* MODE:                                  */
    /* Determines the current edition mode    */
    /******************************************/

    private _Mode: EditMode | null = null;

    /** 
     * @description Gets or sets the current edit mode.
     * Changing mode clears secondary panel state for the departing and arriving modes
     * and resets shared {@link PanelArg}; assign {@link ScndTab} and {@link PanelArg}
     * after setting mode when the navigation should restore them.
     */    
    get Mode(): EditMode | null {
        return this._Mode;
    }

    set Mode(value: EditMode){
        if (this.Mode != value){
            const _prev = this._Mode;
            if (_prev) {
                this.SetScndTab(_prev, null);
                this.SetScndTab(value, null);
                this.PanelArg = null;
            }

            this._Mode = value;
            this._onViewChanged.next();
        }
    }

    /******************************************/
    /* RIGHT PANEL STATUS                     */
    /******************************************/    

    private _rightdefault: string | null = null;
    private _onRightChanged = new Subject<void>();

    /** 
     * @description Observable that emits when the right panel visibility changes.
     */    
    public OnRightChanged = this._onRightChanged.asObservable();
   
    /** 
     * @description Gets or sets the visibility of the right panel.
     */    
    get DefaultPanel(): string | null {
        return this._rightdefault;
    }

    set DefaultPanel(value: string | null){
        const previous = this._rightdefault 
        if (value != previous){
            this._rightdefault = value;
            this._onRightChanged.next();
        }
    }

    /******************************************/
    /* CURRENT TAB STATUS                     */
    /******************************************/    
    
    private _maintab = new Map <EditMode, string | null> ()
    private _scndtab = new Map <EditMode, string | null> ()
    private _panelarg: any | null = null

    private _onTabChanged = new Subject<string | null>();

    /** 
     * @description Observable that emits when the tab changes.
     */    
    public OnTabChanged = this._onTabChanged.asObservable();    

    /** 
     * @description Gets or sets the primary tab.
     * Setting a new tab will emit a change event.
     */   
    get MainTab(): string | null {
        if (!this.Mode){
            return null;            
        }

        return this._maintab.get(this.Mode) || null;        
    }

    SetMainTab(mode: EditMode, value: string | null){
        this._maintab.set(mode, value);
        if (mode == this.Mode){
            this._onTabChanged.next(value);    
        }
    }

    /** 
     * @description Gets or sets the secondary tab.
     * Setting a new tab will emit a change event.
     */   
    get ScndTab(): string | null {
        if (!this.Mode){
            return null;            
        }

        return this._scndtab.get(this.Mode) || null;        
    }

    set ScndTab(value: string | null){
        if (!this.Mode){
            return;
        }

        this.SetScndTab(this.Mode, value);
    }

    /**
     * @description Sets the secondary tab for a specific edit mode.
     * Emits {@link OnTabChanged} only when the mode matches the active {@link Mode}.
     */
    SetScndTab(mode: EditMode, value: string | null): void {
        this._scndtab.set(mode, value);
        if (mode === this.Mode) {
            this._onTabChanged.next(value);
        }
    }

    /** 
     * @description Gets or sets the secondary tab arguments.
     * Setting the tab arguments will emit a change event.
     */   
    get PanelArg(): any | null {
        return this._panelarg;
    } 

    set PanelArg(value: any | null){
        this._panelarg = value;
        this._onTabChanged.next(value);    
    }    

    /******************************************/
    /* PANEL CLOSE REQUEST                    */
    /******************************************/    

    private _closesubscribers = 0;
    private _onCloseRequest = new Subject <void> ();

    /** 
     * @description Observable that emits when a panel close request is made.
     */    
    public OnCloseRequest = {
        subscribe: (callback: () => void): Subscription => {
            this._closesubscribers++;   // increments the number of subscriptors
            const subscription = this._onCloseRequest.asObservable().subscribe(callback);
            
            // override the unsubscribe method to update the subscriber counters
            const original_unsubscribe = subscription.unsubscribe;
            subscription.unsubscribe = () => {
                this._closesubscribers--;   // release the subscriptor
                original_unsubscribe.call(subscription);
            };
            
            return subscription;
        }
    };

    private _onGrantChange = new Subject <boolean> ();
    private OnGrantChange = this._onGrantChange.asObservable();

    /** 
     * @description Emits a response to a close request.
     * @param {boolean} allow - Indicates whether the close request is granted.
     */    
    OnCloseResponse(allow: boolean){
        this._onGrantChange.next(allow);
    }

    /** 
     * @description Waits for responses from all subscribers to the close request and resolves to a boolean.
     * @returns {Promise<boolean>} True if all subscribers allow the view change, otherwise false.
     */    
    async GrantViewChange(): Promise <boolean>{
        return new Promise<boolean>((resolve) => {
            if (this._closesubscribers > 0) {
                const _responses: Array<boolean> = [];       
                
                const _subscription = this.OnGrantChange.subscribe((allow) => {
                    _responses.push(allow);
                    
                    // we must wait until we have a response for each request
                    if (_responses.length === this._closesubscribers) {
                        _subscription.unsubscribe();
                        resolve(_responses.every((allow) => allow));
                    }
                });    
            } else {
                resolve(true);
            }

            this._onCloseRequest.next();
        });
    }

    /******************************/
    /* PLACE PANEL INFORMATION    */
    /******************************/

    private _placepanel: PlacePanel | null = null;

    /** 
     * @description Gets or sets the current place panel.
     */    
    get Panel(): PlacePanel | null{
        return this._placepanel;
    }

    set Panel(value: PlacePanel | null){
        this._placepanel = value;
    }

    /**
     * Applies a {@link PlacePanel} to Mode / ScndTab / PanelArg / Panel and runs
     * `panel.sync` when present. Does not ask leave-guards — use {@link RequestPanel}.
     * @param panel Navigation payload from shells or feature emitters.
     */
    ApplyPanel(panel: PlacePanel): void {
        if ('main' in panel) {
            const _mainpanel = panel.main || null;

            if (_mainpanel === 'view') {
                this.Mode = 'VIEW';
            }

            if (_mainpanel === 'edit') {
                this.Mode = 'EDIT';
            }

            if (_mainpanel === 'cart') {
                this.Mode = 'CART';
            }
        }

        if ('scnd' in panel) {
            this.ScndTab = panel.scnd ?? null;
        }

        if ('args' in panel) {
            this.PanelArg = panel.args ?? null;
        }

        this.Panel = panel;

        if (panel.sync) {
            panel.sync();
        }
    }

    /**
     * Asks leave-guards unless `panel.force`, then {@link ApplyPanel} when allowed.
     * @param panel Navigation payload from shells or feature emitters.
     * @returns Whether the panel was applied.
     */
    async RequestPanel(panel: PlacePanel): Promise<boolean> {
        const _allow = panel.force || await this.GrantViewChange();
        if (_allow) {
            this.ApplyPanel(panel);
        }
        return _allow;
    }

    /******************************/
    /* ONLINE/OFFLINE CONTROL     */
    /******************************/

    /** 
     * @returns {boolean} True if the application is online, otherwise false.
     */    
    get IsOnline(): boolean {
        return this.http.IsOnline;
    }

    /** 
     * @description Observable that emits when the online status changes.
     */    
    public get OnOnline() : Observable <boolean> {
        return this.http.OnOnline;
    }

    /******************************/
    /* SERVICE WORKER UPDATES     */
    /******************************/

    private _swupdate = false;
    
    /** 
     * @description Gets or sets the service worker update status.
     */    
    get SwUpdate(): boolean {
        return this._swupdate;
    }

    set SwUpdate(value: boolean){
        if (value != this._swupdate){
            if (value){
                console.info("[UNPISPAS] Detected new service worker version!");
            }

            this._swupdate = value;
            this._onViewChanged.next();    
        }
    }
    
    /******************************/
    /* MAIN ENTRY POINT           */
    /******************************/

    private CheckDevice(){
        this.Kiosk = this.configuration.get(UppViewConfiguration.kiosk) as boolean || this.platform._isKiosk;

        const _theme = this.configuration.get(UppViewConfiguration.theme) as 'dark' | 'light' | null;
        if (_theme){
            this.Theme = _theme;
        }

        const _zoom = this.configuration.get(UppViewConfiguration.zoom) as number | null;
        if (_zoom){
            this.ZoomLevel = _zoom;
        }

        const _scrollbar = this.configuration.get(UppViewConfiguration.scrollbar);
        if (_scrollbar != null){
            this.Scrollbar = _scrollbar as boolean;
        }

        const _forced = this.configuration.get(UppViewConfiguration.forcedevice) as 'mobile' | 'desktop' | 'auto' | null;
        const _preferred: 'mobile' | 'desktop' | 'auto' =
            (_forced === 'mobile' || _forced === 'desktop' || _forced === 'auto') ? _forced : 'auto';

        // Explicit ?mode=mobile|desktop on this load is applied by platformService first.
        // Do not re-apply a cached preference or it navigates back to the opposite mode.
        const _urlMode = new URLSearchParams(window.location.search).get('mode')?.toLowerCase();
        if (_urlMode !== 'mobile' && _urlMode !== 'desktop'){
            this.ForcedMode = _preferred;
        }

        this._ApplyAppProfile();

        this._subscriptions.push(this.OnKiosk.subscribe(
        () => {
            this.Kiosk = this.platform._isKiosk;
        }));

        this._onViewChanged.next();
    }

    /**
     * Applies {@link AppProfile}: forced mode always (unless `?mode=` wins);
     * default zoom only when the platform is ready, no zoom is stored yet, and zoom is allowed.
     * Zoom waits for {@link platformService.IsReady} because {@link CanZoom} needs device detection.
     */
    private _ApplyAppProfile(): void {
        const _profile = this._appProfile;
        if (!_profile){
            return;
        }

        const _urlMode = new URLSearchParams(window.location.search).get('mode')?.toLowerCase();
        if ((_profile.mode === 'mobile' || _profile.mode === 'desktop')
            && _urlMode !== 'mobile' && _urlMode !== 'desktop'){
            this.ForcedMode = _profile.mode;
        }

        if (_profile.zoom != null && this.platform.IsReady && this.CanZoom){
            const _stored = this.configuration.get(UppViewConfiguration.zoom) as number | null;
            if (_stored == null){
                this.ZoomLevel = _profile.zoom;
            }
        }
    }

    private async _LoadLanguages(): Promise <boolean> {
        const _needed: Array <string> = ['main'];
        if (this.state.Access == 'GUEST'){
            _needed.push('guest');
        }

        const _promises: Array <Promise<boolean>> = [];
        for (const _module of _needed){
            if (this._langModules.has(_module)){
                continue;
            }

            this._langModules.add(_module);
            _promises.push(this.lang.LoadModule(_module));
        }

        if (_promises.length == 0){
            return true;
        }

        const _results = await Promise.all(_promises);
        
        const _allok = _results.every(
        (result) => {
            return result === true
        });

        if (_allok){
            this._onViewChanged.next();
        }

        return _allok;
    }

    /**
     * @constructor
     * @description Initializes the `viewService` by injecting necessary dependencies and setting up initial subscriptions.
     * - **Dependencies**: Injects services for language management (`languageService`), platform-specific logic (`platformService`),
     *   HTTP connectivity (`httpService`), and state management (`stateService`).
     * - **Device Initialization**: Waits for the platform to be ready and checks the device state (e.g., kiosk mode).
     * - **Session Expiration Handling**: Subscribes to session expiration events from `stateService` to reset the view when the session expires.
     * - **Close Request Subscription**: Subscribes to close requests, incrementing a counter to track the number of subscribers who respond to close events.
     * - **Language Loading**: Initiates loading of necessary language modules based on the current user access level.
     * 
     * @param {languageService} lang - Service responsible for managing language modules and localization.
     * @param {platformService} platform - Service providing platform-specific information and state management.
     * @param {httpService} http - Service for managing network connectivity and online status.
     * @param {stateService} state - Service for managing user state and access level, including session expiration events.
     */
    constructor(private lang: languageService, private platform: platformService, private http: httpService, private state: stateService, private config: configService){
        this.platform.ready().then(
        () => {
            this.CheckDevice();
        })

        this._subscriptions.push(this.state.OnExpired.subscribe(
        () => {
            this.View = null;
        }));

        this._LoadLanguages();
    }

    /**
     * @description Unsubscribes from all active subscriptions to prevent memory leaks.
     */    
    ngOnDestroy(){
        for(const _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }
}


  